/* eslint-disable react/jsx-max-depth */
import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import {
   IconSize,
   IconWrapper,
   PersonalizeIcon,
   StrongText,
   StyledFlex,
   Tooltip,
   TooltipPlacement,
   TooltipTrigger,
} from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { useAiAssistantRecordingGlobalState } from '../shared/useAiAssistantRecordingGlobalState';
import { ensureMicrophoneAccess } from '../audio/ensureMicrophoneAccess';
import { useAiAssistantTokensQuotaGuard } from '../shared/useAiAssistantTokensQuotaGuard';
import { MicrophoneIcon } from '../icons/MicrophoneIcon';

import { AiAssistantMainMenuTileItemConsentModalOpener } from './AiAssistantMainMenuTileItemConsentModal.opener';
import { AiAssistantMainMenuTileItemContainer } from './AiAssistantMainMenuTileItemContainer';
import { TileItemPrimaryButton } from './TileItemPrimaryButton';
import { AiAssistantMainMenuTileItemIdleSoundSourceForm } from './soundSource/AiAssistantMainMenuTileItemIdleSoundSourceForm';

const t = translationKeys.mainMenuTileItem;

type AiAssistantMainMenuTileItemIdleProps = { onStartTranscription: () => Promise<boolean> };

export const AiAssistantMainMenuTileItemIdle = (props: AiAssistantMainMenuTileItemIdleProps): JSX.Element => {
   const theme = useTheme();
   const { startRecording } = useAiAssistantRecordingGlobalState();
   const verifyTokensQuota = useAiAssistantTokensQuotaGuard();

   const handleRun = useCallback(async () => {
      if (!(await ensureMicrophoneAccess())) {
         return;
      }

      if (await props.onStartTranscription()) {
         startRecording();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [startRecording, props.onStartTranscription]);

   return (
      <AiAssistantMainMenuTileItemContainer icon={<MicrophoneIcon />} backgroundColor={theme.primaryBackground}>
         <StyledFlex direction={'column'} gap={8} alignItems={'center'} width={'100%'}>
            <StyledFlex direction={'column'} gap={4} alignItems={'center'}>
               <MicrophoneIcon />
               <StyledFlex gap={4}>
                  <StrongText testId={'aiVisitAssistant'}>
                     <Translate>{t.title}</Translate>
                  </StrongText>
                  <Tooltip
                     content={<AiAssistantMainMenuTileItemIdleSoundSourceForm />}
                     testId={'personalize'}
                     placement={TooltipPlacement.RIGHT}
                     trigger={TooltipTrigger.CLICK}
                     maxWidth={'430px'}
                     hideOnOutsideClick
                  >
                     <IconWrapper testId={'settings'} size={IconSize.SMALLEST}>
                        <PersonalizeIcon />
                     </IconWrapper>
                  </Tooltip>
               </StyledFlex>
            </StyledFlex>
            <AiAssistantMainMenuTileItemConsentModalOpener onConfirm={handleRun}>
               {({ openModal }) => (
                  <TileItemPrimaryButton
                     label={t.run}
                     testId={'run'}
                     onClick={async () => {
                        if (await verifyTokensQuota()) {
                           openModal();
                        }
                     }}
                  />
               )}
            </AiAssistantMainMenuTileItemConsentModalOpener>
         </StyledFlex>
      </AiAssistantMainMenuTileItemContainer>
   );
};
